import React, { useState } from "react";
import { toast } from "sonner";
import { X, Copy } from "lucide-react";
import api from "@/api";

const ROLES = [
  ["amministratore", "Amministratore"],
  ["responsabile_cantiere", "Responsabile cantiere"],
  ["impiegato_amministrativo", "Impiegato amministrativo"],
  ["responsabile_documenti", "Responsabile documenti"],
  ["lavoratore", "Lavoratore"],
  ["collaboratore", "Collaboratore esterno"],
];

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copiato negli appunti");
  } catch {
    toast.error("Copia fallita — copia manualmente");
  }
};

export default function InviteModal({ onClose, onCreated, prefill }) {
  const [form, setForm] = useState({
    email: prefill?.email || "",
    name: prefill?.name || "",
    role: "lavoratore",
    societa: "",
  });
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setSaving(true);
    try {
      const { data } = await api.post("/auth/invite", form);
      const url = `${window.location.origin}/invite/${data.token}`;
      setCreated({ ...data, url });
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione invito");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="font-display text-xl font-bold">Nuovo invito</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        {!created ? (
          <form onSubmit={submit}>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Email destinatario</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  placeholder="mario.rossi@aruba.it"
                  data-testid="invite-form-email"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Nome (opzionale)</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  placeholder="Mario Rossi"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Ruolo iniziale</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
                  data-testid="invite-form-role"
                >
                  {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Società (opzionale)</label>
                <input
                  type="text"
                  value={form.societa}
                  onChange={(e) => setForm({ ...form, societa: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  placeholder="Ethos Costruzioni SRL"
                />
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-900">
                💡 L'invito genererà un link che potrai condividere via WhatsApp, email o SMS.
                Il destinatario aprirà il link, imposterà la sua password e sarà subito operativo.
                Il link scade dopo 7 giorni.
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-sm hover:bg-white">Annulla</button>
              <button type="submit" disabled={saving} data-testid="btn-submit-invite" className="px-4 py-2 text-sm font-semibold bg-blue-700 hover:bg-blue-800 text-white rounded-sm disabled:opacity-60">
                {saving ? "Creazione..." : "Genera invito"}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-4 space-y-3">
            <div className="text-center">
              <div className="text-4xl mb-2">✅</div>
              <h3 className="font-display text-lg font-black">Invito creato</h3>
              <p className="text-sm text-slate-600">Condividi questo link con <b>{created.email}</b>:</p>
            </div>
            <div className="p-3 border-2 border-blue-300 bg-blue-50 rounded-sm">
              <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700 mb-1">Link invito</div>
              <div className="font-mono text-xs break-all text-slate-900" data-testid="created-invite-url">{created.url}</div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => copyToClipboard(created.url)}
                className="flex-1 flex items-center justify-center gap-2 py-2 border border-blue-700 text-blue-700 hover:bg-blue-50 font-semibold rounded-sm"
              >
                <Copy className="w-4 h-4" /> Copia link
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Ciao, ecco il link per accedere a Ethos Costruzioni: ${created.url}`)}`}
                target="_blank"
                rel="noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-sm"
              >
                Invia via WhatsApp
              </a>
            </div>
            <button onClick={onClose} className="w-full py-2 text-sm font-semibold border border-slate-300 rounded-sm hover:bg-slate-50">Chiudi</button>
          </div>
        )}
      </div>
    </div>
  );
}
